import { useTheme } from '@emotion/react';
import { useRouter } from 'next/dist/client/router';
import Head from 'next/head';
import { memo } from 'react';
import styled from '@emotion/styled';

import Background from './Background';
import GoogleFonts from './GoogleFonts';

import type { ReactNode } from 'react';
import type { Theme, ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';
import ThemeNextImage from '../NextImage/ThemeNextImage';

const Card = styled('div')`
  ${(props: ThemeProps): CSSObject | undefined => props.theme.components.Card}
`;

interface PageProps {
  pointer: string;
}
type PointerEvents = 'auto' | 'none';

const Page = styled('div')`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: ${(props: PageProps): PointerEvents =>
    props.pointer === 'none' ? 'none' : 'auto'};

  ${(props: ThemeProps): CSSObject | undefined => props.theme.components.Page};
`;

const Footer = styled('footer')`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-top: auto;
  position: relative;
  width: 100%;

  ${(props: ThemeProps): CSSObject | undefined => props.theme.components.Footer}
`;

interface Props {
  children: ReactNode;
  footer?: ReactNode | undefined;
  offsetBackgroundForNavBar?: boolean | undefined;
}

const href = `${process.env['FAVICON_URL'] || ''}`;

function ThemeWrap({
  children,
  footer,
  offsetBackgroundForNavBar = true,
}: Props) {
  const theme: Theme = useTheme();
  const { query } = useRouter() || { query: { pointerEvents: 'auto' } };

  const pointerEvents =
    typeof query['pointerEvents'] === 'undefined' ||
    Array.isArray(query['pointerEvents'])
      ? 'auto'
      : 'none';

  const { bottom } = theme.images;

  const { bottom: bottomResponsive } = theme.responsiveImages || {};

  return (
    <Card>
      <Head>
        <link rel="icon" type="image/x-icon" href={href} />
        <meta name="theme-color" content={theme?.colors?.background} />
      </Head>

      <GoogleFonts />

      <Background offsetBackgroundForNavBar={offsetBackgroundForNavBar} />

      <Page pointer={pointerEvents}>{children}</Page>

      <Footer>
        {footer}

        <ThemeNextImage
          src={bottom || ''}
          alt="Bottom Theme Image"
          mobileSrc={bottomResponsive?.smUrl || ''}
          imageCSSObject={theme.components?.BottomImage}
          desktopSizes="(min-width: 768px) 100vw, 0vw"
          mobileSizes="(max-width: 767px) 100vw, 0vw"
        />
      </Footer>
    </Card>
  );
}

export default memo(ThemeWrap);
